import { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useTheme } from '@/context/ThemeContext';
import { useChatbot } from '@/context/ChatbotContext';
import WebsiteFooter from '@/components/website/WebsiteFooter';

// Lazy load AnimatedBrain for performance
const AnimatedBrain = lazy(() => import('@/components/AnimatedBrain'));

import {
  Menu,
  X,
  ArrowRight,
  ArrowUpRight,
  Code2,
  Cloud,
  BarChart3,
  Shield,
  Cpu,
  ChevronRight,
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Twitter,
  Facebook,
  Star,
  CheckCircle2,
  Users,
  Globe,
  Sparkles,
  Zap,
  Brain,
  Rocket,
  Play,
  Sun,
  Moon,
  Bot,
  Layers,
  Target,
  TrendingUp,
  Award,
  Headphones,
  Smartphone,
  Settings,
  Briefcase,
  ClipboardCheck,
  UserCheck,
  FileCheck,
  Building2,
  Cog,
  GraduationCap,
  Package,
  Newspaper,
  ExternalLink,
  Clock,
  Wallet,
  Lock,
  ShoppingCart,
  Scale,
  Search,
  GitBranch,
  ShieldCheck,
  Server,
  Layout,
  Database,
  Container,
  BadgeCheck
} from 'lucide-react';
import { toast } from 'sonner';

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={countRef}>{count}{suffix}</span>;
};

// AI News Ticker Component
const AINewsTicker = () => {
  const [news, setNews] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API}/ai-news`);
        setNews(response.data.articles || []);
      } catch (error) {
        // Fallback news
        setNews([
          { id: 1, title: "OpenAI Unveils GPT-5 with Revolutionary Reasoning Capabilities", source: "TechCrunch" },
          { id: 2, title: "AI-Powered Code Generation Transforms Software Development", source: "Wired" },
          { id: 3, title: "Enterprise AI Adoption Reaches Record High in 2026", source: "Forbes" },
          { id: 4, title: "AI in Healthcare: Machine Learning Detects Diseases Earlier", source: "MIT Tech Review" },
          { id: 5, title: "Autonomous AI Agents Are Reshaping Business Operations", source: "Bloomberg" },
        ]);
      }
    };

    fetchNews();
    // Refresh news every 5 minutes
    const refreshInterval = setInterval(fetchNews, 5 * 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, []);

  useEffect(() => {
    if (news.length === 0) return;
    // Rotate through news every 5 seconds
    const rotateInterval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % news.length);
    }, 5000);
    return () => clearInterval(rotateInterval);
  }, [news.length]);

  if (news.length === 0) return null;

  const currentNews = news[currentIndex];

  return (
    <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-purple-600 via-violet-600 to-purple-600 text-white py-2">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center gap-3">
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
            <span className="text-xs font-bold uppercase tracking-wider text-purple-200">AI News</span>
          </div>
          <div className="h-4 w-px bg-purple-400/50"></div>
          <div className="flex-1 overflow-hidden">
            <div 
              key={currentIndex}
              className="flex items-center gap-2"
              style={{animation: 'fadeIn 0.5s ease-out'}}
            >
              <span className="text-sm font-medium truncate">{currentNews?.title}</span>
              <span className="text-xs text-purple-300 flex-shrink-0 hidden sm:inline">— {currentNews?.source}</span>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
            {news.slice(0, 5).map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  idx === currentIndex ? 'bg-white w-4' : 'bg-purple-400/50 hover:bg-purple-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Navbar Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [betaModalOpen, setBetaModalOpen] = useState(false);
  const [betaPassword, setBetaPassword] = useState('');
  const [betaError, setBetaError] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { theme, toggleTheme } = useTheme();
  const { openChatbot } = useChatbot();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleProductClick = (e, product) => {
    e.preventDefault();
    setSelectedProduct(product);
    setBetaPassword('');
    setBetaError('');
    setBetaModalOpen(true);
    setProductsOpen(false);
  };

  const handleBetaSubmit = () => {
    if (betaPassword === '8269') {
      window.open(selectedProduct.href, '_blank', 'noopener,noreferrer');
      setBetaModalOpen(false);
      setBetaPassword('');
      setSelectedProduct(null);
    } else {
      setBetaError('Incorrect password. Please try again.');
    }
  };

  const navLinks = [
    { name: 'IT Solutions', href: '/#services' },
    { name: 'AI Services', href: '/#services' },
    { name: 'AI Talent', href: '/ai-talent', isRoute: true },
    { name: 'AI News', href: '/ai-news', isRoute: true },
    { name: 'Our Work', href: '/our-work', isRoute: true },
    { name: 'Clients', href: '/clients', isRoute: true },
    { name: 'Career', href: '/careers', isRoute: true },
    { name: 'Contact', href: '/#contact' },
  ];

  const products = [
    { name: 'MyHR Orbit', description: 'AI-Powered HR Management', href: 'https://myhrorbit.com', icon: Users, color: 'bg-blue-500', external: true },
    { name: 'MyFinance Orbit', description: 'AI-Powered Financial Management', href: 'https://myfinanceorbit.com', icon: Wallet, color: 'bg-green-500', external: true },
    { name: 'MyProcurement Orbit', description: 'AI-Powered Procurement', href: 'https://procurement.aurousorbit.ai', icon: ShoppingCart, color: 'bg-orange-500', external: true },
    { name: 'MyGovernance Orbit', description: 'AI-Powered Governance', href: 'https://governance.aurousorbit.ai', icon: Scale, color: 'bg-purple-500', external: true },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled 
        ? 'bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl shadow-lg shadow-slate-900/5' 
        : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-2 group">
            <img 
              src={scrolled 
                ? "https://customer-assets.emergentagent.com/job_01e8900b-dd68-4220-97d5-adc9134fd5fc/artifacts/o7zqp6wt_D469D6E7-D15E-45AE-A15A-66CBE7FDF89F.png"
                : "https://customer-assets.emergentagent.com/job_aurous-ecosystem/artifacts/x28qd6xo_30882183-9DD0-40B4-98FF-3BB81A460020.png"
              }
              alt="Aurous" 
              className="h-10 w-auto transition-all"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              // Handle hash links that need navigation (like /#contact)
              const handleHashClick = (e) => {
                if (link.href.startsWith('/#')) {
                  e.preventDefault();
                  const hash = link.href.substring(1); // Remove leading /
                  if (window.location.pathname === '/') {
                    // Already on homepage, just scroll
                    const element = document.querySelector(hash);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  } else {
                    // Navigate to homepage with hash
                    window.location.href = link.href;
                  }
                }
              };

              return link.isRoute ? (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all hover:bg-white/10 ${
                    scrolled ? 'text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400' : 'text-white/80 hover:text-white'
                  }`}
                >
                  {link.name}
                </Link>
              ) : link.onClick ? (
                <button
                  key={link.name}
                  onClick={link.onClick}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all hover:bg-white/10 ${
                    scrolled ? 'text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400' : 'text-white/80 hover:text-white'
                  }`}
                >
                  {link.name}
                </button>
              ) : (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={handleHashClick}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all hover:bg-white/10 ${
                    scrolled ? 'text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400' : 'text-white/80 hover:text-white'
                  }`}
                >
                  {link.name}
                </a>
              );
            })}
            
            {/* Products Dropdown */}
            <div className="relative">
              <button
                onClick={() => setProductsOpen(!productsOpen)}
                onBlur={() => setTimeout(() => setProductsOpen(false), 200)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all hover:bg-white/10 flex items-center gap-1 ${
                  scrolled ? 'text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400' : 'text-white/80 hover:text-white'
                }`}
              >
                Products
                <ChevronRight className={`w-4 h-4 transition-transform ${productsOpen ? 'rotate-90' : ''}`} />
              </button>
              
              {productsOpen && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden z-50">
                  {/* AurousOrbit.ai Header */}
                  <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                        <Globe className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="font-bold text-white">AurousOrbit.ai</div>
                        <div className="text-xs text-indigo-200">AI Products by Aurous</div>
                      </div>
                    </div>
                    <a 
                      href="https://aurousorbit.ai" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="mt-3 flex items-center justify-center gap-2 w-full py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white text-sm font-medium transition-colors"
                    >
                      Visit Website
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  
                  {/* Products List */}
                  <div className="py-2">
                    <div className="px-4 py-2 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wide">
                      Our Products
                    </div>
                    {products.map((product) => (
                      <button
                        key={product.name}
                        onClick={(e) => handleProductClick(e, product)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left"
                      >
                        <div className={`w-10 h-10 rounded-xl ${product.color} flex items-center justify-center`}>
                          <product.icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-slate-900 dark:text-white flex items-center gap-1">
                            {product.name}
                            <ExternalLink className="w-3 h-3 text-slate-400" />
                          </div>
                          <div className="text-sm text-slate-500 dark:text-slate-400">{product.description}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* CTA Buttons + Theme Toggle */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className={`p-2.5 rounded-xl transition-all ${
                scrolled 
                  ? 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700' 
                  : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              {theme === 'dark' ? (
                <Sun className={`w-5 h-5 ${scrolled ? 'text-yellow-500' : 'text-yellow-300'}`} />
              ) : (
                <Moon className={`w-5 h-5 ${scrolled ? 'text-slate-600' : 'text-white'}`} />
              )}
            </button>
            
            <Link to="/login">
              <Button variant="ghost" className={`rounded-xl ${scrolled ? 'text-slate-600 dark:text-slate-300' : 'text-white'}`}>
                Login
              </Button>
            </Link>
            <Button 
              onClick={() => openChatbot('menu')}
              className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-600/25 hover:shadow-blue-600/40 transition-all"
            >
              Talk to an Expert
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Mobile Menu */}
          <div className="lg:hidden flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg ${scrolled ? 'bg-slate-100 dark:bg-slate-800' : 'bg-white/10'}`}
            >
              {theme === 'dark' ? <Sun className="w-5 h-5 text-yellow-500" /> : <Moon className="w-5 h-5 text-white" />}
            </button>
            <button className="p-2" onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? (
                <X className={`w-6 h-6 ${scrolled ? 'text-slate-900 dark:text-white' : 'text-white'}`} />
              ) : (
                <Menu className={`w-6 h-6 ${scrolled ? 'text-slate-900 dark:text-white' : 'text-white'}`} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isOpen && (
          <div className="lg:hidden bg-white dark:bg-slate-900 rounded-2xl shadow-xl mt-2 p-4 border border-slate-200 dark:border-slate-800">
            {navLinks.map((link) => {
              const handleMobileHashClick = (e) => {
                setIsOpen(false);
                if (link.href.startsWith('/#')) {
                  e.preventDefault();
                  const hash = link.href.substring(1);
                  if (window.location.pathname === '/') {
                    const element = document.querySelector(hash);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  } else {
                    window.location.href = link.href;
                  }
                }
              };

              return link.isRoute ? (
                <Link key={link.name} to={link.href} className="block py-3 px-4 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl" onClick={() => setIsOpen(false)}>
                  {link.name}
                </Link>
              ) : link.onClick ? (
                <button key={link.name} onClick={() => { link.onClick(); setIsOpen(false); }} className="block w-full text-left py-3 px-4 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl">
                  {link.name}
                </button>
              ) : (
                <a key={link.name} href={link.href} className="block py-3 px-4 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl" onClick={handleMobileHashClick}>
                  {link.name}
                </a>
              );
            })}
            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800 flex gap-2">
              <Link to="/login" className="flex-1">
                <Button variant="outline" className="w-full rounded-xl">Login</Button>
              </Link>
              <button onClick={() => { openChatbot('menu'); setIsOpen(false); }} className="flex-1">
                <Button className="w-full rounded-xl bg-blue-600">Talk to Expert</Button>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Beta Access Modal */}
      {betaModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setBetaModalOpen(false)}
          />
          
          {/* Modal */}
          <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 p-6 text-center">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white">Beta Access Required</h3>
              <p className="text-indigo-200 text-sm mt-1">
                {selectedProduct?.name} is currently in beta testing
              </p>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Enter Beta Password
                </label>
                <input
                  type="password"
                  value={betaPassword}
                  onChange={(e) => {
                    setBetaPassword(e.target.value);
                    setBetaError('');
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleBetaSubmit()}
                  placeholder="Enter password..."
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  autoFocus
                />
                {betaError && (
                  <p className="text-red-500 text-sm mt-2">{betaError}</p>
                )}
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setBetaModalOpen(false)}
                  className="flex-1 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBetaSubmit}
                  className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium hover:from-indigo-700 hover:to-purple-700 transition-colors"
                >
                  Access Beta
                </button>
              </div>
              
              <p className="text-center text-xs text-slate-500 dark:text-slate-400 mt-4">
                Contact your administrator if you need beta access credentials
              </p>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section - Enterprise-Focused
const HeroSection = () => {
  const { openChatbot } = useChatbot();
  
  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950">
        {/* Animated gradient orbs */}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-blue-500/30 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-indigo-500/20 rounded-full blur-[100px] animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[150px]"></div>
        
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
        
        {/* Floating particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[
            { left: '15%', top: '20%', delay: '0s', duration: '8s' },
            { left: '45%', top: '10%', delay: '1s', duration: '12s' },
            { left: '75%', top: '35%', delay: '2s', duration: '10s' },
            { left: '25%', top: '65%', delay: '0.5s', duration: '9s' },
            { left: '85%', top: '55%', delay: '1.5s', duration: '11s' },
            { left: '55%', top: '80%', delay: '3s', duration: '7s' },
            { left: '5%', top: '45%', delay: '2.5s', duration: '13s' },
            { left: '95%', top: '15%', delay: '4s', duration: '8s' },
            { left: '35%', top: '90%', delay: '1.2s', duration: '14s' },
            { left: '65%', top: '50%', delay: '0.8s', duration: '10s' },
          ].map((particle, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white/20 rounded-full animate-float"
              style={{
                left: particle.left,
                top: particle.top,
                animationDelay: particle.delay,
                animationDuration: particle.duration
              }}
            ></div>
          ))}
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border border-blue-500/30 rounded-full mb-8 backdrop-blur-sm">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-blue-300 text-sm font-medium">Enterprise Technology Partner</span>
            </div>

            {/* Main Heading - Enterprise Focused */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
              Enterprise Software
              <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-indigo-400">
                Delivered by AI-Augmented Teams
              </span>
            </h1>

            {/* Description - Value Driven */}
            <p className="text-lg sm:text-xl text-slate-400 mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              Experienced engineering teams with AI-enhanced workflows. We deliver enterprise platforms, cloud migrations, and digital transformation for regulated industries.
            </p>

            {/* CTA Buttons - Clear Primary/Secondary */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-16">
              <Button 
                size="lg" 
                onClick={() => openChatbot('consultation')}
                className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-lg h-14 px-8 rounded-2xl shadow-lg shadow-blue-600/25 hover:shadow-blue-600/40 transition-all group"
              >
                Book a Consultation
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Link to="/sales-deck">
                <Button size="lg" variant="outline" className="w-full sm:w-auto text-white border-white/20 hover:bg-white/10 text-lg h-14 px-8 rounded-2xl backdrop-blur-sm">
                  <FileCheck className="w-5 h-5 mr-2" />
                  Download Capability Deck
                </Button>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-green-400" />
                <span>SOC 2 Compliant</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-blue-400" />
                <span>Malaysia Digital Certified</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-cyan-400" />
                <span>Global Delivery</span>
              </div>
            </div>
          </div>

          {/* Right Content - 3D Animated AI Brain */}
          <div className="hidden lg:flex items-center justify-center">
            <div className="relative w-full max-w-lg">
              <Suspense fallback={
                <div className="w-full h-[500px] flex items-center justify-center">
                  <div className="w-64 h-64 rounded-full bg-gradient-to-br from-cyan-500/20 to-violet-500/20 animate-pulse blur-sm"></div>
                </div>
              }>
                <AnimatedBrain />
              </Suspense>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white dark:from-slate-900 to-transparent"></div>
    </section>
  );
};

// Value Proposition Strip - NEW SECTION
const ValuePropositionStrip = () => {
  const propositions = [
    { icon: UserCheck, text: 'AI-skilled contract engineers' },
    { icon: Users, text: 'Managed AI & automation teams' },
    { icon: Building2, text: 'Enterprise & regulated industry support' },
    { icon: Bot, text: 'Human + AI productivity model' },
    { icon: Zap, text: 'Fast deployment, measurable outcomes' },
  ];

  return (
    <section className="py-12 bg-gradient-to-r from-blue-600 to-indigo-600">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-center gap-6 lg:gap-12">
          {propositions.map((item, index) => {
            const Icon = item.icon;
            return (
              <div key={index} className="flex items-center gap-3 text-white/90">
                <Icon className="w-5 h-5 text-blue-200" />
                <span className="text-sm font-medium whitespace-nowrap">{item.text}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// AI Technology Showcase Section - Visual storytelling
const AITechnologyShowcase = () => {
  const showcaseItems = [
    {
      image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxlbnRlcnByaXNlJTIwdGVjaG5vbG9neSUyMHRlYW0lMjB3b3JraW5nJTIwZGF0YSUyMGFuYWx5dGljcyUyMGRhc2hib2FyZHxlbnwwfHx8fDE3Nzk3ODE2MTJ8MA&ixlib=rb-4.1.0&q=85',
      title: 'Collaborative Teams',
      description: 'Diverse teams working together on innovative solutions'
    },
    {
      image: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxlbnRlcnByaXNlJTIwdGVjaG5vbG9neSUyMHRlYW0lMjB3b3JraW5nJTIwZGF0YSUyMGFuYWx5dGljcyUyMGRhc2hib2FyZHxlbnwwfHx8fDE3Nzk3ODE2MTJ8MA&ixlib=rb-4.1.0&q=85',
      title: 'Data Analytics',
      description: 'Expert analysis and data-driven decision making'
    },
    {
      image: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxlbnRlcnByaXNlJTIwdGVjaG5vbG9neSUyMHRlYW0lMjB3b3JraW5nJTIwZGF0YSUyMGFuYWx5dGljcyUyMGRhc2hib2FyZHxlbnwwfHx8fDE3Nzk3ODE2MTJ8MA&ixlib=rb-4.1.0&q=85',
      title: 'Modern Workspace',
      description: 'State-of-the-art tech environment for innovation'
    },
    {
      image: 'https://images.unsplash.com/photo-1769740333462-9a63bfa914bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBtZWV0aW5nJTIwcm9vbSUyMHByZXNlbnRhdGlvbiUyMGNvcnBvcmF0ZSUyMG9mZmljZXxlbnwwfHx8fDE3Nzk3ODE2MTN8MA&ixlib=rb-4.1.0&q=85',
      title: 'Client Meetings',
      description: 'Professional strategy sessions with enterprise clients'
    },
    {
      image: 'https://images.unsplash.com/photo-1758691737045-3ece61135061?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHRlYW0lMjBkaXNjdXNzaW9uJTIwc3RyYXRlZ3klMjBtZWV0aW5nJTIwZ2xhc3MlMjBvZmZpY2V8ZW58MHx8fHwxNzc5NzgxMzQ0fDA&ixlib=rb-4.1.0&q=85',
      title: 'Agile Planning',
      description: 'Sprint planning and agile methodology in action'
    },
    {
      image: 'https://images.pexels.com/photos/6804068/pexels-photo-6804068.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      title: 'Tech Hub',
      description: 'Cutting-edge development environment'
    },
    {
      image: 'https://images.unsplash.com/photo-1769739576456-0aefcff3f4b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBtZWV0aW5nJTIwcm9vbSUyMHByZXNlbnRhdGlvbiUyMGNvcnBvcmF0ZSUyMG9mZmljZXxlbnwwfHx8fDE3Nzk3ODE2MTN8MA&ixlib=rb-4.1.0&q=85',
      title: 'Strategy Sessions',
      description: 'Executive briefings and roadmap planning'
    },
    {
      image: 'https://images.pexels.com/photos/7693706/pexels-photo-7693706.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      title: 'Business Analytics',
      description: 'Real-time insights and performance tracking'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
            Our Team
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            World-Class{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
              Engineering Teams
            </span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            500+ skilled professionals delivering enterprise solutions with AI-enhanced workflows
          </p>
        </div>

        {/* Animated Image Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {showcaseItems.map((item, index) => (
            <div
              key={index}
              className="group relative overflow-hidden rounded-2xl cursor-pointer"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              {/* Image */}
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
              </div>
              
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />
              
              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6 transform transition-transform duration-500 group-hover:translate-y-0 translate-y-2">
                <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                <p className="text-sm text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100">
                  {item.description}
                </p>
              </div>

              {/* Animated border */}
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-cyan-500/50 rounded-2xl transition-colors duration-300" />
              
              {/* Corner accent */}
              <div className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-cyan-500/20 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <Sparkles className="w-4 h-4 text-cyan-400" />
              </div>
            </div>
          ))}
        </div>

        {/* Animated Stats Bar */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Brain, value: 'AI-First', label: 'Delivery Model' },
            { icon: Users, value: '500+', label: 'Tech Professionals' },
            { icon: Globe, value: '4', label: 'Global Offices' },
            { icon: Zap, value: '24/7', label: 'Support Available' },
          ].map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="text-center p-6 rounded-2xl bg-slate-800/50 border border-slate-700/50 hover:border-cyan-500/30 transition-all group"
              >
                <Icon className="w-8 h-8 text-cyan-400 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-slate-400">{stat.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Industries Section - Enterprise Focus
const IndustriesSection = () => {
  const industries = [
    { name: 'Banking & Finance', icon: '🏦', clients: 'Maybank, CIMB, RHB, Public Bank' },
    { name: 'Insurance', icon: '🛡️', clients: 'AIA, Prudential, Great Eastern, Allianz' },
    { name: 'Government', icon: '🏛️', clients: 'LHDN, EPF, KWAP, MDEC' },
    { name: 'Telecommunications', icon: '📡', clients: 'Celcom, Maxis, Digi, TM' },
    { name: 'Healthcare', icon: '🏥', clients: 'Leading Hospital Groups' },
    { name: 'Energy & Utilities', icon: '⚡', clients: 'Petronas, TNB' },
  ];

  return (
    <section id="industries" className="py-20 bg-slate-50 dark:bg-slate-800/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <Badge className="mb-4 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400">
            Industries
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">
            Deep Domain Expertise
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            17+ years serving regulated industries with complex compliance requirements.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {industries.map((industry, i) => (
            <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl p-6 text-center hover:shadow-lg transition-all border border-slate-100 dark:border-slate-700">
              <div className="text-3xl mb-3">{industry.icon}</div>
              <h3 className="font-semibold text-slate-900 dark:text-white text-sm mb-2">{industry.name}</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">{industry.clients}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Real Results Section - Enterprise Case Studies
const RealResultsSection = () => {
  const caseStudies = [
    {
      metric: '60%',
      metricLabel: 'Faster QA Cycles',
      industry: 'Banking',
      client: 'Major Malaysian Bank',
      problem: 'Manual regression testing taking 2 weeks per release, causing deployment delays.',
      solution: 'Implemented AI-powered test automation with intelligent test selection and parallel execution.',
      outcome: 'Reduced testing cycle from 14 days to 3 days, enabling weekly releases.',
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      metric: '2M+',
      metricLabel: 'Daily Transactions',
      industry: 'Finance',
      client: 'Fortune 500 Financial Services',
      problem: 'Legacy payment system unable to handle growing transaction volumes.',
      solution: 'Built cloud-native, high-availability payment platform with real-time processing.',
      outcome: 'Platform now handles 2M+ daily transactions with 99.99% uptime.',
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      metric: '40%',
      metricLabel: 'Cost Reduction',
      industry: 'Insurance',
      client: 'Regional Insurance Group',
      problem: 'Aging on-premise infrastructure with high maintenance costs.',
      solution: 'Executed cloud migration with containerization and infrastructure optimization.',
      outcome: 'Reduced infrastructure costs by 40% while improving system performance.',
      gradient: 'from-green-500 to-emerald-500',
    },
  ];

  return (
    <section className="py-24 bg-white dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-xs font-semibold tracking-[0.2em] text-blue-600 dark:text-blue-400 uppercase mb-3">
            Case Studies
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">
            Real Results Delivered
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            Measurable outcomes from enterprise engagements across regulated industries.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {caseStudies.map((study, i) => (
            <div key={i} className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl overflow-hidden hover:shadow-xl transition-all border border-slate-100 dark:border-slate-700/50 group">
              {/* Metric Header */}
              <div className={`bg-gradient-to-r ${study.gradient} p-6`}>
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-5xl font-bold text-white mb-1">{study.metric}</div>
                    <div className="text-sm text-white/80">{study.metricLabel}</div>
                  </div>
                  <Badge className="bg-white/20 text-white border-0 text-xs">
                    {study.industry}
                  </Badge>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6 space-y-4">
                <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                  {study.client}
                </div>
                
                <div className="space-y-3">
                  <div>
                    <div className="text-xs font-semibold text-red-500 dark:text-red-400 uppercase mb-1">Challenge</div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{study.problem}</p>
                  </div>
                  
                  <div>
                    <div className="text-xs font-semibold text-blue-500 dark:text-blue-400 uppercase mb-1">Solution</div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{study.solution}</p>
                  </div>
                  
                  <div>
                    <div className="text-xs font-semibold text-green-500 dark:text-green-400 uppercase mb-1">Outcome</div>
                    <p className="text-sm text-slate-700 dark:text-slate-300 font-medium">{study.outcome}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/our-work">
            <Button variant="outline" className="border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800">
              View All 183+ Projects
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

// Why Choose Aurous - Trust & Credibility
const WhyChooseSection = () => {
  const stats = [
    { value: '17+', label: 'Years Experience', icon: Award },
    { value: '500+', label: 'Projects Delivered', icon: Briefcase },
    { value: '100+', label: 'Engineers', icon: Users },
    { value: '4', label: 'Global Offices', icon: Globe },
  ];

  const technologies = [
    'AWS', 'Azure', 'Google Cloud', 'Kubernetes', 'Python', 'Java', 'React', 'Node.js', 'TensorFlow', 'OpenAI', 'LangChain', 'PostgreSQL'
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <Badge className="mb-4 bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
            Why Aurous
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Why Companies Choose Aurous
          </h2>
          <p className="text-lg text-slate-400">
            Enterprise-grade delivery with startup agility.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div key={i} className="text-center p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm">
                <Icon className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
                <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-slate-400">{stat.label}</div>
              </div>
            );
          })}
        </div>

        {/* Technology Stack */}
        <div className="text-center">
          <p className="text-slate-400 text-sm mb-6 uppercase tracking-wide">Technology Stack</p>
          <div className="flex flex-wrap justify-center gap-3">
            {technologies.map((tech, i) => (
              <span key={i} className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm text-slate-300">
                {tech}
              </span>
            ))}
          </div>
        </div>

        {/* Certifications */}
        <div className="mt-12 flex flex-wrap justify-center gap-6 text-sm text-slate-400">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-green-400" />
            <span>Malaysia Digital Certified</span>
          </div>
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-blue-400" />
            <span>MOF Registered</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-cyan-400" />
            <span>ISO Compliant</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// Enterprise Delivery Model Section
const DeliveryModelSection = () => {
  const steps = [
    {
      step: '01',
      title: 'Discovery & Architecture',
      description: 'Deep-dive into business requirements, technical landscape, and define solution architecture.',
      icon: Search,
      color: 'from-blue-500 to-blue-600',
    },
    {
      step: '02',
      title: 'AI/Platform Engineering',
      description: 'Build core platform components with AI integration, following enterprise coding standards.',
      icon: Code2,
      color: 'from-purple-500 to-purple-600',
    },
    {
      step: '03',
      title: 'Enterprise Integration',
      description: 'Connect with existing systems, APIs, and data sources ensuring seamless interoperability.',
      icon: GitBranch,
      color: 'from-orange-500 to-orange-600',
    },
    {
      step: '04',
      title: 'Security & Compliance',
      description: 'Rigorous security testing, compliance validation, and audit documentation.',
      icon: ShieldCheck,
      color: 'from-green-500 to-green-600',
    },
    {
      step: '05',
      title: 'Deployment & Optimization',
      description: 'Production deployment with monitoring, continuous improvement, and support handover.',
      icon: Rocket,
      color: 'from-cyan-500 to-cyan-600',
    },
  ];

  return (
    <section className="py-24 bg-white dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with Image */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] text-blue-600 dark:text-blue-400 uppercase mb-3">
              How We Deliver
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Our Enterprise Delivery Model
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400">
              A proven methodology refined across 500+ enterprise projects. Our teams follow a rigorous process to ensure quality, security, and on-time delivery.
            </p>
          </div>
          <div className="hidden lg:flex justify-end">
            <img 
              src="https://images.unsplash.com/photo-1581092583537-20d51b4b4f1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxlbnRlcnByaXNlJTIwdGVjaG5vbG9neSUyMHRlYW0lMjB3b3JraW5nJTIwZGF0YSUyMGFuYWx5dGljcyUyMGRhc2hib2FyZHxlbnwwfHx8fDE3Nzk3ODE2MTJ8MA&ixlib=rb-4.1.0&q=85" 
              alt="Team planning session"
              className="rounded-2xl shadow-xl max-h-[350px] w-auto object-cover"
            />
          </div>
        </div>

        {/* Timeline Steps */}
        <div className="relative">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 via-purple-500 via-orange-500 via-green-500 to-cyan-500 opacity-20"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={i} className="relative group">
                  {/* Step Card */}
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-6 border border-slate-100 dark:border-slate-700/50 hover:border-blue-300 dark:hover:border-blue-600 transition-all h-full">
                    {/* Step Number */}
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    
                    <div className="text-xs font-bold text-slate-400 dark:text-slate-500 mb-2">
                      STEP {step.step}
                    </div>
                    
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                      {step.title}
                    </h3>
                    
                    <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                  
                  {/* Arrow connector for desktop */}
                  {i < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-24 -right-3 z-10">
                      <ChevronRight className="w-6 h-6 text-slate-300 dark:text-slate-600" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// Enterprise Technology Stack Section
const TechStackSection = () => {
  const categories = [
    {
      name: 'AI Models',
      icon: Brain,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      techs: ['OpenAI GPT-4', 'Claude', 'Gemini', 'LangChain', 'LlamaIndex'],
    },
    {
      name: 'Cloud Platforms',
      icon: Cloud,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      techs: ['AWS', 'Microsoft Azure', 'Google Cloud', 'Alibaba Cloud'],
    },
    {
      name: 'Backend',
      icon: Server,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      techs: ['Python', 'Node.js', 'FastAPI', 'Java', '.NET'],
    },
    {
      name: 'Frontend',
      icon: Layout,
      color: 'text-cyan-500',
      bgColor: 'bg-cyan-500/10',
      techs: ['React', 'TypeScript', 'Next.js', 'Vue.js', 'Angular'],
    },
    {
      name: 'Data & Storage',
      icon: Database,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
      techs: ['PostgreSQL', 'MongoDB', 'Redis', 'Pinecone', 'Elasticsearch'],
    },
    {
      name: 'DevOps & Infra',
      icon: Container,
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
      techs: ['Docker', 'Kubernetes', 'Terraform', 'GitHub Actions', 'Jenkins'],
    },
  ];

  return (
    <section className="py-24 bg-slate-50 dark:bg-slate-800/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-xs font-semibold tracking-[0.2em] text-purple-600 dark:text-purple-400 uppercase mb-3">
            Technology
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">
            Enterprise AI & Technology Stack
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            Modern, scalable technologies chosen for enterprise reliability.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((cat, i) => {
            const Icon = cat.icon;
            return (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-100 dark:border-slate-700/50">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-lg ${cat.bgColor} flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${cat.color}`} />
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-white">{cat.name}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {cat.techs.map((tech, j) => (
                    <span 
                      key={j} 
                      className="px-3 py-1.5 bg-slate-100 dark:bg-slate-700/50 text-slate-700 dark:text-slate-300 text-sm rounded-lg border border-slate-200 dark:border-slate-600"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Security & Compliance Section
const SecurityComplianceSection = () => {
  const certifications = [
    {
      name: 'SOC 2 Ready',
      description: 'Security controls aligned with SOC 2 Type II requirements',
      icon: ShieldCheck,
      color: 'from-green-500 to-emerald-500',
    },
    {
      name: 'ISO 27001',
      description: 'Information security management best practices',
      icon: Award,
      color: 'from-blue-500 to-blue-600',
    },
    {
      name: 'Malaysia Digital',
      description: 'Certified digital technology company by MDEC',
      icon: BadgeCheck,
      color: 'from-purple-500 to-purple-600',
    },
    {
      name: 'MOF Registered',
      description: 'Ministry of Finance registered vendor',
      icon: Building2,
      color: 'from-orange-500 to-orange-600',
    },
    {
      name: 'AI Governance',
      description: 'Responsible AI with audit trails and explainability',
      icon: Brain,
      color: 'from-cyan-500 to-cyan-600',
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] text-green-400 uppercase mb-3">
              Enterprise Grade
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Security & Compliance
            </h2>
            <p className="text-lg text-slate-400 mb-8 leading-relaxed">
              Built for regulated industries. Our solutions meet the stringent security, privacy, and compliance requirements of banking, insurance, healthcare, and government sectors.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-white">End-to-End Encryption</div>
                  <div className="text-sm text-slate-400">Data encrypted in transit and at rest with enterprise-grade standards</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-white">Audit Trail & Logging</div>
                  <div className="text-sm text-slate-400">Complete activity logs for compliance reporting and forensics</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-white">PDPA & GDPR Ready</div>
                  <div className="text-sm text-slate-400">Data protection controls aligned with privacy regulations</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Certifications Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {certifications.map((cert, i) => {
              const Icon = cert.icon;
              return (
                <div 
                  key={i} 
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-center hover:bg-white/10 transition-all"
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${cert.color} flex items-center justify-center mx-auto mb-3`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="font-bold text-white text-sm mb-1">{cert.name}</div>
                  <div className="text-xs text-slate-400 leading-relaxed">{cert.description}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// Engagement Models Section
const EngagementModelsSection = () => {
  const models = [
    {
      title: 'Dedicated Engineering Teams',
      description: 'Full-time engineers embedded with your team, working exclusively on your projects with complete knowledge transfer.',
      icon: Users,
      features: ['Dedicated resources', 'Full integration', 'Long-term engagement'],
      color: 'from-blue-500 to-blue-600',
    },
    {
      title: 'Project-Based Delivery',
      description: 'Fixed-scope, fixed-timeline projects with clear milestones and deliverables managed end-to-end by our team.',
      icon: Target,
      features: ['Fixed scope & cost', 'Defined timeline', 'Complete ownership'],
      color: 'from-purple-500 to-purple-600',
    },
    {
      title: 'AI Platform Implementation',
      description: 'Deploy our AurousOrbit platforms (HR, Finance, Procurement) customized to your enterprise requirements.',
      icon: Cpu,
      features: ['Pre-built platform', 'Rapid deployment', 'Customization'],
      color: 'from-green-500 to-green-600',
    },
    {
      title: 'Transformation Programs',
      description: 'Multi-year strategic partnerships for large-scale digital transformation and AI adoption initiatives.',
      icon: TrendingUp,
      features: ['Strategic roadmap', 'Phased delivery', 'Change management'],
      color: 'from-orange-500 to-orange-600',
    },
  ];

  return (
    <section className="py-24 bg-white dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-xs font-semibold tracking-[0.2em] text-orange-600 dark:text-orange-400 uppercase mb-3">
            Work With Us
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">
            Flexible Engagement Models
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            Choose the model that fits your project scope and organizational needs.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {models.map((model, i) => {
            const Icon = model.icon;
            return (
              <div 
                key={i} 
                className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-8 border border-slate-100 dark:border-slate-700/50 hover:border-blue-300 dark:hover:border-blue-600 transition-all group"
              >
                <div className="flex items-start gap-5">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${model.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                      {model.title}
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400 mb-4 leading-relaxed">
                      {model.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {model.features.map((feature, j) => (
                        <span 
                          key={j}
                          className="inline-flex items-center gap-1 text-xs px-3 py-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-full text-slate-600 dark:text-slate-300"
                        >
                          <CheckCircle2 className="w-3 h-3 text-green-500" />
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Enterprise Products Section - AurousOrbit Platform
const EnterpriseProductsSection = () => {
  const products = [
    {
      name: 'MyHR Orbit',
      description: 'AI-powered HR management platform with automated recruitment, performance analytics, and workforce planning.',
      href: 'https://myhrorbit.com',
      icon: Users,
      color: 'bg-blue-500',
      gradient: 'from-blue-500 to-blue-600',
    },
    {
      name: 'MyFinance Orbit',
      description: 'Intelligent financial management with AI-driven forecasting, automated invoicing, and compliance reporting.',
      href: 'https://myfinanceorbit.com',
      icon: Wallet,
      color: 'bg-green-500',
      gradient: 'from-green-500 to-emerald-500',
    },
    {
      name: 'MyProcurement Orbit',
      description: 'Smart procurement with AI vendor evaluation, automated RFQ processes, and spend analytics.',
      href: 'https://procurement.aurousorbit.ai',
      icon: ShoppingCart,
      color: 'bg-orange-500',
      gradient: 'from-orange-500 to-amber-500',
    },
    {
      name: 'MyGovernance Orbit',
      description: 'Enterprise governance platform with risk assessment, compliance monitoring, and policy management.',
      href: 'https://governance.aurousorbit.ai',
      icon: Scale,
      color: 'bg-purple-500',
      gradient: 'from-purple-500 to-violet-500',
    },
  ];

  const [betaModalOpen, setBetaModalOpen] = useState(false);
  const [betaPassword, setBetaPassword] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [passwordError, setPasswordError] = useState(false);

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setBetaModalOpen(true);
    setBetaPassword('');
    setPasswordError(false);
  };

  const handleBetaAccess = () => {
    if (betaPassword === '8269') {
      window.open(selectedProduct.href, '_blank');
      setBetaModalOpen(false);
    } else {
      setPasswordError(true);
    }
  };

  return (
    <section className="py-24 bg-slate-50 dark:bg-slate-800/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 dark:bg-indigo-900/50 rounded-full mb-4">
            <span className="text-indigo-600 dark:text-indigo-400 text-sm font-semibold">AurousOrbit.ai</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">
            Enterprise AI Platforms
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            Purpose-built AI platforms that complement our consulting services.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((product, i) => {
            const Icon = product.icon;
            return (
              <div
                key={i}
                onClick={() => handleProductClick(product)}
                className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-lg transition-all cursor-pointer group"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${product.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">{product.name}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 leading-relaxed">{product.description}</p>
                <span className="text-sm font-semibold text-blue-600 dark:text-blue-400 flex items-center gap-1 group-hover:gap-2 transition-all">
                  Learn More
                  <ExternalLink className="w-4 h-4" />
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Beta Access Modal */}
      <Dialog open={betaModalOpen} onOpenChange={setBetaModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-blue-500" />
              Beta Access Required
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {selectedProduct?.name} is currently in beta. Enter the access code to continue.
            </p>
            <input
              type="password"
              placeholder="Enter beta access code"
              value={betaPassword}
              onChange={(e) => {
                setBetaPassword(e.target.value);
                setPasswordError(false);
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleBetaAccess()}
              className={`w-full px-4 py-3 rounded-xl border ${passwordError ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'} bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {passwordError && (
              <p className="text-sm text-red-500">Invalid access code. Please try again.</p>
            )}
            <Button onClick={handleBetaAccess} className="w-full bg-gradient-to-r from-blue-600 to-indigo-600">
              Access {selectedProduct?.name}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

// Strong Conversion Block - Before Footer
const ConversionSection = () => {
  const { openChatbot } = useChatbot();

  return (
    <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600 relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 opacity-10">
        <img 
          src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxlbnRlcnByaXNlJTIwdGVjaG5vbG9neSUyMHRlYW0lMjB3b3JraW5nJTIwZGF0YSUyMGFuYWx5dGljcyUyMGRhc2hib2FyZHxlbnwwfHx8fDE3Nzk3ODE2MTJ8MA&ixlib=rb-4.1.0&q=85" 
          alt="Team collaboration"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
          Need AI-Ready Engineering Teams?
        </h2>
        <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
          Whether you're building AI products, modernizing legacy systems, or scaling your engineering capacity — let's discuss how we can help.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            size="lg" 
            onClick={() => openChatbot('consultation')}
            className="bg-white text-blue-600 hover:bg-blue-50 text-lg h-14 px-8 rounded-2xl shadow-lg"
          >
            Book a Consultation
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <Link to="/sales-deck">
            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-lg h-14 px-8 rounded-2xl">
              <FileCheck className="w-5 h-5 mr-2" />
              Download Capability Deck
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

// Trusted By Section with Logos
const TrustedBySection = () => {
  // Logo.dev API token (same as ClientsPage)
  const LOGO_DEV_TOKEN = 'pk_X-1ZO13GSgeOoUrIuJ6GMQ';
  
  // All enterprise clients with domains for logo.dev API
  const clients = [
    // Banks & Financial Institutions
    { name: 'Public Bank', domain: 'pbebank.com' },
    { name: 'Maybank', domain: 'maybank.com' },
    { name: 'Bank Rakyat', domain: 'bankrakyat.com.my' },
    { name: 'Standard Chartered', domain: 'sc.com' },
    { name: 'RHB Bank', domain: 'rhbgroup.com' },
    { name: 'Alliance Bank', domain: 'alliancebank.com.my' },
    { name: 'Bursa Malaysia', domain: 'bursamalaysia.com' },
    // Insurance & Takaful
    { name: 'AIA Malaysia', domain: 'aia.com.my' },
    { name: 'Great Eastern', domain: 'greateasternlife.com' },
    { name: 'Great Eastern Takaful', domain: 'greateasterntakaful.com' },
    { name: 'Prudential', domain: 'prudential.com.my' },
    { name: 'Prudential BSN', domain: 'prudential.com' },
    { name: 'MetLife', domain: 'metlife.com' },
    { name: 'Sun Life Malaysia', domain: 'sunlife.com' },
    { name: 'AXA Malaysia', domain: 'axa.com' },
    { name: 'FWD Insurance', domain: 'fwd.com' },
    { name: 'Pacific Insurance', domain: 'pacificinsurance.com.my' },
    { name: 'Gibraltar BSN', domain: 'gibraltar-bsn.com.my' },
    { name: 'Takaful Malaysia', domain: 'takaful-malaysia.com.my' },
    { name: 'Takaful Ikhlas', domain: 'takaful-ikhlas.com.my' },
    { name: 'MLRe Malaysian Life', domain: 'mlre.com.my' },
    { name: 'Tune Protect', domain: 'tuneprotect.com' },
    // Telecommunications & Technology
    { name: 'TM', domain: 'tm.com.my' },
    { name: 'Astro', domain: 'astro.com.my' },
    { name: 'Naza Communications', domain: 'naza.com.my' },
    { name: 'LTIMindtree', domain: 'ltimindtree.com' },
    { name: 'MolPay', domain: 'razer.com' },
    // Conglomerates & Corporations
    { name: 'YTL', domain: 'ytl.com' },
    { name: 'Sime Darby', domain: 'simedarby.com' },
    { name: 'PPB Group', domain: 'ppbgroup.com' },
    { name: 'AAM Group', domain: 'aam.com.my' },
    // Aviation & Logistics
    { name: 'Malaysia Airlines', domain: 'malaysiaairlines.com' },
    { name: 'DHL', domain: 'dhl.com' },
    { name: 'POS Malaysia', domain: 'pos.com.my' },
    // Energy & Industrial
    { name: 'Orsted', domain: 'orsted.com' },
    { name: 'Forbes Marshall', domain: 'forbesmarshall.com' },
    { name: 'Quality Kiosk', domain: 'qualitykiosk.com' },
  ];

  // Get logo URL from logo.dev API
  const getLogoUrl = (domain) => {
    return `https://img.logo.dev/${domain}?token=${LOGO_DEV_TOKEN}&size=128&format=png`;
  };

  return (
    <section className="py-16 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <p className="text-xs font-semibold tracking-[0.2em] text-slate-500 dark:text-slate-400 uppercase mb-2">
            Trusted by Global Enterprises
          </p>
          <p className="text-slate-400 text-sm">
            Serving 100+ enterprise clients across banking, insurance, and government sectors
          </p>
        </div>
        
        {/* Scrolling Client Logos */}
        <div className="relative overflow-hidden">
          {/* Gradient Masks */}
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-slate-50 dark:from-slate-900/50 to-transparent z-10"></div>
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-slate-50 dark:from-slate-900/50 to-transparent z-10"></div>
          
          <div className="flex animate-scroll-fast gap-16 items-center">
            {[...clients, ...clients].map((client, i) => (
              <div 
                key={i} 
                className="flex-shrink-0 group"
              >
                <div className="flex items-center justify-center w-24 h-14 opacity-90 hover:opacity-100 hover:scale-110 transition-all duration-300">
                  <img 
                    src={getLogoUrl(client.domain)} 
                    alt={client.name}
                    className="w-20 h-12 object-contain"
                    loading="lazy"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes scroll-fast {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-scroll-fast {
          animation: scroll-fast 30s linear infinite;
        }
        .animate-scroll-fast:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
};

// Our Clients Section - Trusted Partners
const OurClientsSection = () => {
  // Official client list from Aurous website - use actual hosted images
  const clients = [
    // Main Featured Clients (with Aurous hosted logos)
    { name: 'Public Bank', logo: 'assets/img/brand-logo/aurous_client_public_bank.png', domain: 'publicbank.com.my' },
    { name: 'MetLife', logo: 'assets/img/brand-logo/am_metlife.png', domain: 'metlife.com.my' },
    { name: 'MLRe Malaysian Life', logo: 'assets/img/brand-logo/aurous_client_MLRe.png', domain: 'mlre.com.my' },
    { name: 'Bursa Malaysia', logo: 'assets/img/brand-logo/aurous_client_bursa_malaysia.png', domain: 'bursamalaysia.com' },
    { name: 'Sun Life Malaysia', logo: 'assets/img/brand-logo/aurous_client_sunlife_malaysia.png', domain: 'sunlifemalaysia.com' },
    { name: 'Bank Rakyat', logo: 'assets/img/brand-logo/aurous_client_bank_rakyat.png', domain: 'bankrakyat.com.my' },
    { name: 'Standard Chartered', logo: 'assets/img/brand-logo/aurous_client_standard_chartered_bank.png', domain: 'sc.com' },
    { name: 'PPB Group', logo: 'assets/img/brand-logo/aurous_client_ppb_group_berhad.png', domain: 'ppbgroup.com' },
    { name: 'YTL', logo: 'assets/img/brand-logo/aurous_client_ytl.png', domain: 'ytl.com' },
    { name: 'Great Eastern', logo: 'assets/img/brand-logo/aurous_client_great-eastern.png', domain: 'greateasternlife.com' },
    { name: 'AIA Malaysia', logo: 'assets/img/brand-logo/aurous_client_aia.png', domain: 'aia.com.my' },
    { name: 'LTIMindtree', logo: 'assets/img/brand-logo/aurous_client_lti.png', domain: 'ltimindtree.com' },
    { name: 'DHL', logo: 'assets/img/brand-logo/aurous_client_dhl.png', domain: 'dhl.com' },
    { name: 'Pacific Insurance', logo: 'assets/img/brand-logo/aurous_client_pacific-insurance.png', domain: 'pacificinsurance.com.my' },
    { name: 'Forbes Marshall', logo: 'assets/img/brand-logo/aurous_client_forbes-marshall.png', domain: 'forbesmarshall.com' },
    { name: 'RHB Bank', logo: 'assets/img/brand-logo/aurous_client_rhb_bank.png', domain: 'rhbgroup.com' },
    { name: 'TM', logo: 'assets/img/brand-logo/aurous_client_tm.png', domain: 'tm.com.my' },
    { name: 'Sime Darby', logo: 'assets/img/brand-logo/aurous_client_sime_darby.png', domain: 'simedarby.com' },
    { name: 'AXA Malaysia', logo: 'assets/img/brand-logo/aurous_client_axa.png', domain: 'axa.com.my' },
    { name: 'Naza Communications', logo: 'assets/img/brand-logo/aurous_client_naza_communications.png', domain: 'naza.com.my' },
    { name: 'Prudential BSN', logo: 'assets/img/brand-logo/aurous_client_prudential-bsn.png', domain: 'prudentialbsn.com.my' },
    { name: 'Takaful Malaysia', logo: 'assets/img/brand-logo/aurous_client_takaful-malaysia.png', domain: 'takaful-malaysia.com.my' },
    { name: 'POS Malaysia', logo: 'assets/img/brand-logo/aurous_client_pos-malaysia.png', domain: 'pos.com.my' },
    { name: 'Prudential', logo: 'assets/img/brand-logo/aurous_client_prudential.png', domain: 'prudential.com.my' },
    { name: 'Takaful Ikhlas', logo: 'assets/img/brand-logo/aurous_client_takaful-ikhlas.png', domain: 'takafulikhlas.com.my' },
    { name: 'Gibraltar BSN', logo: 'assets/img/brand-logo/aurous_client_gibraltar-bsn.png', domain: 'gibraltarbsn.com' },
    { name: 'Maybank', logo: 'assets/img/brand-logo/aurous_client_may-bank.png', domain: 'maybank.com' },
    { name: 'FWD Insurance', logo: 'assets/img/brand-logo/aurous_client_fwd_insurance.png', domain: 'fwd.com.my' },
    { name: 'AAM Group', logo: 'assets/img/brand-logo/aurous_client_aam_group.png', domain: 'aamgroup.com' },
    { name: 'AIA General', logo: 'assets/img/brand-logo/aurous_client_aia_general.png', domain: 'aia.com.my' },
    { name: 'Alliance Bank', logo: 'assets/img/brand-logo/aurous_client_alliance_bank.png', domain: 'alliancebank.com.my' },
    { name: 'Astro', logo: 'assets/img/brand-logo/aurous_client_astro.png', domain: 'astro.com.my' },
    { name: 'Great Eastern Takaful', logo: 'assets/img/brand-logo/aurous_client_great_eastern_takaful.png', domain: 'greateasterntakaful.com' },
    { name: 'Malaysia Airlines', logo: 'assets/img/brand-logo/aurous_client_malaysia_airlines.png', domain: 'malaysiaairlines.com' },
    { name: 'MolPay', logo: 'assets/img/brand-logo/aurous_client_mol_pay.png', domain: 'molpay.com' },
    { name: 'MS First Capital', logo: 'assets/img/brand-logo/aurous_client_ms_first_capital.png', domain: 'msfirstcapital.com' },
    { name: 'Orsted', logo: 'assets/img/brand-logo/aurous_client_orsted.png', domain: 'orsted.com' },
    { name: 'Quality Kiosk', logo: 'assets/img/brand-logo/aurous_client_quality_kiosk.png', domain: 'qualitykiosk.com' },
    { name: 'Syarikat Takaful', logo: 'assets/img/brand-logo/aurous_client_syarikat_takaful_malaysia.png', domain: 'takaful-malaysia.com.my' },
    { name: 'Tune Protect', logo: 'assets/img/brand-logo/aurous_client_tune_protect_malaysia.png', domain: 'tuneprotect.com' },
  ];
  
  // Split into two rows
  const row1 = clients.slice(0, 20);
  const row2 = clients.slice(20);
  
  // Logo URL function - prefer Aurous hosted, fallback to Google favicon
  const getLogoUrl = (client) => {
    // Use Aurous website hosted logos
    return `https://www.aurousconsultancy.com/${client.logo}`;
  };
  
  // Fallback to Google favicon
  const getFallbackLogoUrl = (domain) => {
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
  };
  
  return (
    <section className="py-20 bg-gradient-to-b from-slate-50 to-white dark:from-slate-800/50 dark:to-slate-900 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 hover:bg-blue-100">
            Trusted Partners
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">
            Our Valued Clients
          </h2>
          <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Empowering Malaysia's leading enterprises with AI-powered IT solutions across banking, insurance, telecommunications, and more.
          </p>
        </div>
        
        {/* Logo Grid with Names */}
        <div className="space-y-6">
          {/* Row 1 */}
          <div className="flex flex-wrap justify-center items-center gap-3 md:gap-4">
            {row1.map((client, index) => (
              <div 
                key={`${client.name}-${index}`}
                className="group flex flex-col items-center justify-center w-28 h-24 md:w-32 md:h-28 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-100 dark:border-slate-700 p-3 transition-all hover:shadow-md hover:scale-105 hover:border-blue-200 dark:hover:border-blue-700"
                title={client.name}
              >
                <div className="flex-1 flex items-center justify-center">
                  <img 
                    src={getLogoUrl(client)} 
                    alt={client.name}
                    className="h-10 w-auto max-w-[80px] object-contain"
                    onError={(e) => {
                      // Try Google favicon as fallback
                      e.target.src = getFallbackLogoUrl(client.domain);
                      e.target.onerror = () => {
                        e.target.style.display = 'none';
                        e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                      };
                    }}
                  />
                  <span 
                    className="hidden w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg items-center justify-center text-white font-bold text-xs"
                    style={{display: 'none'}}
                  >
                    {client.name.substring(0, 2)}
                  </span>
                </div>
                <p className="mt-1 text-[10px] md:text-xs font-medium text-slate-600 dark:text-slate-400 text-center leading-tight truncate w-full group-hover:text-blue-600 dark:group-hover:text-blue-400">
                  {client.name}
                </p>
              </div>
            ))}
          </div>
          
          {/* Row 2 */}
          <div className="flex flex-wrap justify-center items-center gap-3 md:gap-4">
            {row2.map((client, index) => (
              <div 
                key={`${client.name}-rev-${index}`}
                className="group flex flex-col items-center justify-center w-28 h-24 md:w-32 md:h-28 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-100 dark:border-slate-700 p-3 transition-all hover:shadow-md hover:scale-105 hover:border-blue-200 dark:hover:border-blue-700"
                title={client.name}
              >
                <div className="flex-1 flex items-center justify-center">
                  <img 
                    src={getLogoUrl(client)} 
                    alt={client.name}
                    className="h-10 w-auto max-w-[80px] object-contain"
                    onError={(e) => {
                      // Try Google favicon as fallback
                      e.target.src = getFallbackLogoUrl(client.domain);
                      e.target.onerror = () => {
                        e.target.style.display = 'none';
                        e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                      };
                    }}
                  />
                  <span 
                    className="hidden w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg items-center justify-center text-white font-bold text-xs"
                    style={{display: 'none'}}
                  >
                    {client.name.substring(0, 2)}
                  </span>
                </div>
                <p className="mt-1 text-[10px] md:text-xs font-medium text-slate-600 dark:text-slate-400 text-center leading-tight truncate w-full group-hover:text-blue-600 dark:group-hover:text-blue-400">
                  {client.name}
                </p>
              </div>
            ))}
          </div>
        </div>
        
        <div className="text-center mt-12">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            <span className="font-semibold text-blue-600 dark:text-blue-400">50+</span> enterprise clients across Malaysia trust us with their digital transformation
          </p>
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  return (
    <section className="py-16 bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="text-4xl md:text-5xl font-bold text-white mb-2">
              <AnimatedCounter end={15} suffix="+" />
            </div>
            <p className="text-blue-200 text-sm md:text-base">Years of Experience</p>
          </div>
          <div className="text-center">
            <div className="text-4xl md:text-5xl font-bold text-white mb-2">
              <AnimatedCounter end={380} suffix="+" />
            </div>
            <p className="text-blue-200 text-sm md:text-base">Projects Delivered</p>
          </div>
          <div className="text-center">
            <div className="text-4xl md:text-5xl font-bold text-white mb-2">
              <AnimatedCounter end={100} suffix="+" />
            </div>
            <p className="text-blue-200 text-sm md:text-base">Active Clients</p>
          </div>
          <div className="text-center">
            <div className="text-4xl md:text-5xl font-bold text-white mb-2">
              <AnimatedCounter end={4} suffix="+" />
            </div>
            <p className="text-blue-200 text-sm md:text-base">Office Locations</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// What We Do Section
const WhatWeDoSection = () => {
  return (
    <section className="py-20 bg-slate-50 dark:bg-slate-800">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Badge className="mb-4 bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 hover:bg-blue-100">
          What We Do
        </Badge>
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-6">
          Complete IT Solutions & AI-Powered Teams
        </h2>
        <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
          Aurous Consultancy delivers comprehensive IT solutions and AI-powered teams. With 15+ years of experience serving Fortune 500 companies, we combine traditional IT excellence with cutting-edge AI capabilities. From product development to AI integration, we help organizations transform and scale with confidence.
        </p>
      </div>
    </section>
  );
};

// IT Solutions Section (Traditional Services)
const ITSolutionsSection = () => {
  const itServices = [
    {
      icon: Code2,
      title: 'Product Development',
      description: 'End-to-end software development services covering the complete SDLC cycle. We build scalable, secure, and high-performance applications.',
      gradient: 'from-blue-500 to-cyan-500',
      features: ['Custom Software Development', 'Web & Mobile Applications', 'API Development', 'Legacy Modernization']
    },
    {
      icon: Settings,
      title: 'System Enhancements',
      description: 'Upgrade and optimize your existing systems for better performance, security, and user experience.',
      gradient: 'from-purple-500 to-pink-500',
      features: ['Performance Optimization', 'Security Upgrades', 'Feature Enhancements', 'System Integration']
    },
    {
      icon: Briefcase,
      title: 'Technology Consulting',
      description: 'Strategic IT consulting to help you make informed technology decisions and drive digital transformation.',
      gradient: 'from-orange-500 to-red-500',
      features: ['IT Strategy & Roadmap', 'Digital Transformation', 'Architecture Review', 'Technology Assessment']
    },
    {
      icon: BarChart3,
      title: 'Data Analytics & BI',
      description: 'Transform your data into actionable insights with our business intelligence and analytics solutions.',
      gradient: 'from-green-500 to-emerald-500',
      features: ['Data Warehousing', 'BI Dashboards', 'Predictive Analytics', 'Data Visualization']
    },
    {
      icon: Cloud,
      title: 'Cloud Infrastructure',
      description: 'Design, migrate, and manage cloud infrastructure on AWS, Azure, and Google Cloud platforms.',
      gradient: 'from-indigo-500 to-blue-500',
      features: ['Cloud Migration', 'Infrastructure Design', 'DevOps & CI/CD', 'Cloud Security']
    },
    {
      icon: ClipboardCheck,
      title: 'Quality Assurance',
      description: 'Comprehensive QA and testing services to ensure your software meets the highest quality standards.',
      gradient: 'from-teal-500 to-cyan-500',
      features: ['Manual & Automated Testing', 'Performance Testing', 'Security Testing', 'Test Automation']
    },
  ];

  return (
    <section id="it-solutions" className="py-24 bg-slate-50 dark:bg-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="mb-4 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100">
            IT Solutions
          </Badge>
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white mb-6">
            Comprehensive{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
              IT Services
            </span>
          </h2>
          <p className="text-xl text-slate-600 dark:text-slate-400">
            15+ years of delivering enterprise-grade IT solutions for businesses of all sizes.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {itServices.map((service, index) => {
            const Icon = service.icon;
            return (
              <Card 
                key={index}
                className="group border-0 shadow-lg hover:shadow-2xl transition-all duration-500 bg-white dark:bg-slate-900 overflow-hidden"
              >
                <CardContent className="p-6">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">{service.title}</h3>
                  <p className="text-slate-600 dark:text-slate-400 mb-4 text-sm leading-relaxed">{service.description}</p>
                  
                  {/* Features list */}
                  <ul className="space-y-2">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Core Services Section - Enterprise Focused
const ServicesSection = () => {
  const { openChatbot } = useChatbot();
  
  const services = [
    {
      icon: UserCheck,
      title: 'AI-Augmented Engineering Teams',
      valueProposition: 'Scale your engineering capacity with senior developers who leverage AI for 3x productivity gains.',
      bestFor: 'Enterprises needing to accelerate product development without compromising quality.',
      gradient: 'from-blue-500 to-cyan-500',
      bgGradient: 'from-blue-500/10 to-cyan-500/10',
      link: '#contact',
    },
    {
      icon: Users,
      title: 'Managed Delivery Pods',
      valueProposition: 'Complete project teams with built-in PM, architects, and engineers delivering fixed outcomes.',
      bestFor: 'Organizations with defined projects but limited internal capacity to execute.',
      gradient: 'from-purple-500 to-pink-500',
      bgGradient: 'from-purple-500/10 to-pink-500/10',
      link: '#contact',
    },
    {
      icon: Cog,
      title: 'Enterprise AI Integration',
      valueProposition: 'Transform workflows with production-ready AI solutions, not just pilots that never scale.',
      bestFor: 'Companies ready to move from AI experimentation to enterprise-wide deployment.',
      gradient: 'from-orange-500 to-red-500',
      bgGradient: 'from-orange-500/10 to-red-500/10',
      link: '#contact',
    },
    {
      icon: Shield,
      title: 'Compliance & Governance',
      valueProposition: 'AI adoption with built-in audit trails, risk controls, and regulatory compliance.',
      bestFor: 'Regulated industries like banking, insurance, and healthcare requiring AI governance.',
      gradient: 'from-green-500 to-emerald-500',
      bgGradient: 'from-green-500/10 to-emerald-500/10',
      link: '#contact',
    },
  ];

  return (
    <section id="services" className="py-24 bg-white dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="mb-4 bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 hover:bg-blue-100">
            Solutions
          </Badge>
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white mb-6">
            Enterprise{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              Technology Solutions
            </span>
          </h2>
          <p className="text-xl text-slate-600 dark:text-slate-400">
            Proven delivery models that scale with your business needs.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <Card 
                key={index}
                className="group border-0 shadow-lg hover:shadow-2xl transition-all duration-500 bg-white dark:bg-slate-800 overflow-hidden h-full"
              >
                <CardContent className="p-8">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.bgGradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center shadow-lg`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">{service.title}</h3>
                  
                  {/* Value Proposition */}
                  <p className="text-slate-600 dark:text-slate-400 mb-4 leading-relaxed">{service.valueProposition}</p>
                  
                  {/* Best For */}
                  <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 mb-6">
                    <div className="flex items-start gap-2">
                      <Target className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Best For</span>
                        <p className="text-sm text-slate-700 dark:text-slate-300 mt-1">{service.bestFor}</p>
                      </div>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={() => openChatbot('consultation')}
                    variant="outline" 
                    className={`w-full border-2 hover:bg-gradient-to-r hover:${service.gradient} hover:text-white hover:border-transparent transition-all`}
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// AI Talent Pool Preview Section
const TalentPoolSection = () => {
  const { openChatbot } = useChatbot();
  
  const featuredTalent = [
    { name: 'Alex R.', role: 'Senior AI/ML Engineer', experience: '8 years', skills: ['Python', 'TensorFlow', 'LangChain'], availability: 'available' },
    { name: 'Sarah M.', role: 'ML Solutions Architect', experience: '10 years', skills: ['MLflow', 'Azure ML', 'Spark'], availability: 'available' },
    { name: 'Michael T.', role: 'AI Integration Engineer', experience: '4 years', skills: ['LangChain', 'OpenAI', 'FastAPI'], availability: 'available' },
    { name: 'Emily W.', role: 'Principal AI Engineer', experience: '12 years', skills: ['PyTorch', 'Transformers', 'AWS'], availability: 'engaged' },
  ];
  
  return (
    <section className="py-24 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[150px]"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[150px]"></div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="mb-4 bg-blue-500/20 text-blue-300 border-blue-500/30 hover:bg-blue-500/30">
            Our Talent Network
          </Badge>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            AI{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
              Talent Pool
            </span>
          </h2>
          <p className="text-xl text-slate-400">
            Access our network of pre-vetted AI engineers and ML experts ready to join your team.
          </p>
        </div>

        {/* Talent Preview Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {featuredTalent.map((talent, index) => (
            <div key={index} className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-blue-500/30 transition-colors group">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                  {talent.name.charAt(0)}
                </div>
                <div>
                  <h4 className="font-semibold text-white">{talent.name}</h4>
                  <p className="text-sm text-blue-400">{talent.role}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-3 text-sm text-slate-400">
                <Briefcase className="w-4 h-4" />
                <span>{talent.experience}</span>
                <span className={`ml-auto px-2 py-0.5 rounded-full text-xs ${
                  talent.availability === 'available' 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {talent.availability === 'available' ? 'Available' : 'Engaged'}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {talent.skills.map((skill, idx) => (
                  <span key={idx} className="px-2 py-1 bg-white/5 rounded text-xs text-slate-300">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div className="flex flex-wrap justify-center gap-12 mb-12">
          <div className="text-center">
            <p className="text-4xl font-bold text-white">500+</p>
            <p className="text-sm text-slate-400">AI Professionals</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold text-white">50+</p>
            <p className="text-sm text-slate-400">Skill Categories</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold text-white">100+</p>
            <p className="text-sm text-slate-400">Available Now</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold text-white">98%</p>
            <p className="text-sm text-slate-400">Client Satisfaction</p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/ai-talent">
              <Button size="lg" className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-lg h-14 px-8 rounded-2xl">
                Explore Full Talent Pool
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline" 
              className="text-white border-white/20 hover:bg-white/10 text-lg h-14 px-8 rounded-2xl"
              onClick={() => openChatbot('menu')}
            >
              <Users className="w-5 h-5 mr-2" />
              Discuss Requirements
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Products Section - Advanced Tech Design
const ProductsSection = () => {
  const { openChatbot } = useChatbot();
  const products = [
    {
      icon: FileCheck,
      title: 'MyDoc Orbit',
      tagline: 'AI Documentation Tool',
      description: 'Intelligent documentation platform that automatically generates, organizes, and maintains technical documentation using AI.',
      features: ['Auto-generate documentation', 'Smart content organization', 'Version control integration', 'Multi-format export'],
      gradient: 'from-cyan-400 via-blue-500 to-purple-600',
      glowColor: 'cyan',
      status: 'Coming Soon'
    },
    {
      icon: Target,
      title: 'ProjectPulse',
      tagline: 'IT Project Risk Calculator',
      description: 'AI-powered risk assessment tool that analyzes and calculates potential risks in any IT project. Get actionable insights to mitigate risks.',
      features: ['Risk scoring & analysis', 'Predictive risk modeling', 'Mitigation recommendations', 'Real-time monitoring'],
      gradient: 'from-purple-400 via-pink-500 to-red-500',
      glowColor: 'purple',
      status: 'Coming Soon'
    },
  ];

  return (
    <section id="products" className="py-24 relative overflow-hidden bg-slate-950">
      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Circuit pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
        
        {/* Gradient orbs */}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-cyan-500/20 rounded-full blur-[150px] animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-purple-500/20 rounded-full blur-[150px] animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-pink-500/10 rounded-full blur-[100px]"></div>
        
        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white/30 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${8 + Math.random() * 10}s`
            }}
          ></div>
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6 backdrop-blur-sm">
            <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
            <span className="text-purple-300 text-sm font-medium">Our Products</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            AI{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 animate-gradient">
              Products
            </span>
          </h2>
          <p className="text-xl text-slate-400">
            Next-generation AI tools designed to transform how you work.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {products.map((product, index) => {
            const Icon = product.icon;
            return (
              <div key={index} className="group relative">
                {/* Animated border gradient */}
                <div className={`absolute -inset-0.5 bg-gradient-to-r ${product.gradient} rounded-3xl blur opacity-30 group-hover:opacity-75 transition duration-500 group-hover:duration-200 animate-gradient`}></div>
                
                {/* Card */}
                <div className="relative h-full bg-slate-900/90 backdrop-blur-xl rounded-3xl p-8 border border-white/10 transition-all duration-500 group-hover:translate-y-[-4px] group-hover:shadow-2xl">
                  {/* Glow effect */}
                  <div className={`absolute top-0 right-0 w-32 h-32 bg-${product.glowColor}-500/20 rounded-full blur-[60px] group-hover:bg-${product.glowColor}-500/40 transition-all duration-500`}></div>
                  
                  {/* Header */}
                  <div className="relative flex items-start justify-between mb-8">
                    {/* Animated Icon */}
                    <div className="relative">
                      <div className={`absolute inset-0 bg-gradient-to-r ${product.gradient} rounded-2xl blur-lg opacity-50 group-hover:opacity-100 transition-opacity duration-500`}></div>
                      <div className={`relative w-16 h-16 bg-gradient-to-r ${product.gradient} rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    
                    {/* Status Badge */}
                    <div className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full backdrop-blur-sm">
                      <span className="text-xs font-medium text-purple-300">{product.status}</span>
                    </div>
                  </div>
                  
                  {/* Title & Tagline */}
                  <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-slate-300 transition-all duration-300">
                    {product.title}
                  </h3>
                  <p className={`text-sm font-semibold bg-gradient-to-r ${product.gradient} bg-clip-text text-transparent mb-4`}>
                    {product.tagline}
                  </p>
                  
                  {/* Description */}
                  <p className="text-slate-400 mb-8 leading-relaxed">
                    {product.description}
                  </p>
                  
                  {/* Features with tech styling */}
                  <div className="grid grid-cols-2 gap-3 mb-8">
                    {product.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 bg-white/5 rounded-lg border border-white/5 group-hover:border-white/10 transition-colors">
                        <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${product.gradient}`}></div>
                        <span className="text-xs text-slate-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* CTA Button */}
                  <button onClick={() => openChatbot('menu')} className="block w-full">
                    <div className={`relative w-full py-4 px-6 rounded-xl font-semibold text-white overflow-hidden group/btn cursor-pointer`}>
                      {/* Button gradient background */}
                      <div className={`absolute inset-0 bg-gradient-to-r ${product.gradient} transition-all duration-300`}></div>
                      {/* Shine effect */}
                      <div className="absolute inset-0 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-500">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:translate-x-full transition-transform duration-1000"></div>
                      </div>
                      <span className="relative flex items-center justify-center gap-2">
                        Get Early Access
                        <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom section with tech styling */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col items-center p-8 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-sm">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-purple-400" />
              <span className="text-white font-semibold">Be the First to Know</span>
            </div>
            <p className="text-slate-400 mb-6 max-w-md">
              Join our early access list and get exclusive updates on our AI products.
            </p>
            <button onClick={() => openChatbot('menu')}>
              <Button size="lg" className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 border-0 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all">
                Request Early Access
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// AI Workforce Transformation Section - NEW
const WorkforceTransformationSection = () => {
  const { openChatbot } = useChatbot();
  const services = [
    { icon: BarChart3, title: 'AI impact assessment on roles' },
    { icon: Users, title: 'Workforce redesign planning' },
    { icon: Bot, title: 'AI + human hybrid team models' },
    { icon: Target, title: 'Skill gap mapping' },
    { icon: Briefcase, title: 'Transition strategy' },
  ];

  return (
    <section id="managed-teams" className="py-24 bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-[100px]"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-[100px]"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <Badge className="mb-4 bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 border-blue-500/30">
              New Service
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              AI Workforce{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                Transformation
              </span>
            </h2>
            <p className="text-lg text-slate-300 mb-8 leading-relaxed">
              We help organizations redesign their technology teams for the AI era. Transform how your teams work with AI-powered processes and modern delivery models.
            </p>
            
            <div className="space-y-4 mb-10">
              {services.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="flex items-center gap-4 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-blue-400" />
                    </div>
                    <span className="text-white font-medium">{item.title}</span>
                  </div>
                );
              })}
            </div>
            
            <button onClick={() => openChatbot('booking')}>
              <Button size="lg" className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-lg h-14 px-8 rounded-2xl">
                Request AI Workforce Assessment
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </button>
          </div>
          
          {/* Right Visual */}
          <div className="hidden lg:block">
            <div className="relative">
              <div className="w-full aspect-square rounded-3xl bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-white/10 p-8 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-32 h-32 mx-auto mb-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-2xl shadow-blue-500/30">
                    <GraduationCap className="w-16 h-16 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Future-Ready Teams</h3>
                  <p className="text-slate-400">AI-powered workforce solutions</p>
                </div>
              </div>
              
              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 p-4 bg-white dark:bg-slate-800 rounded-xl shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 dark:text-white">40%</p>
                    <p className="text-xs text-slate-500">Faster Delivery</p>
                  </div>
                </div>
              </div>
              
              <div className="absolute -bottom-4 -left-4 p-4 bg-white dark:bg-slate-800 rounded-xl shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 dark:text-white">AI-First</p>
                    <p className="text-xs text-slate-500">Methodology</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section - UPDATED
const AboutSection = () => {
  const { openChatbot } = useChatbot();
  return (
    <section id="about" className="py-24 bg-white dark:bg-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Image/Visual */}
          <div className="relative py-12 px-8">
            <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
              <div className="aspect-[4/3] bg-gradient-to-br from-blue-600 to-indigo-700 p-8 flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-white/20 flex items-center justify-center backdrop-blur">
                    <Brain className="w-12 h-12" />
                  </div>
                  <h3 className="text-3xl font-bold mb-2">17+ Years</h3>
                  <p className="text-blue-100">of IT Excellence</p>
                </div>
              </div>
            </div>
            
            {/* Stats cards */}
            <div className="absolute bottom-0 left-0 p-5 bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700 z-20">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900 dark:text-white">98%</p>
                  <p className="text-xs text-slate-500">Client Retention</p>
                </div>
              </div>
            </div>
            
            <div className="absolute top-0 right-0 p-5 bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700 z-20">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900 dark:text-white">4+</p>
                  <p className="text-xs text-slate-500">Global Offices</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div>
            <Badge className="mb-4 bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400">About Us</Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white mb-6">
              About{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                Aurous Consultancy
              </span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
              Aurous Consultancy is a technology teams and solutions partner focused on AI-powered services. We help organizations scale engineering capacity while improving delivery efficiency through AI-enabled practices.
            </p>
            <p className="text-lg text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">
              Our model combines skilled professionals, structured delivery frameworks, and AI productivity tools to produce measurable results. We support enterprises, regulated industries, and high-growth technology programs with contract engineers, managed teams, and AI-aligned services.
            </p>

            <button onClick={() => openChatbot('menu')}>
              <Button size="lg" className="rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
                Get Started
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Why Clients Work With Us - Trust Section
const TrustSection = () => {
  const reasons = [
    { icon: UserCheck, title: 'AI-ready technical teams' },
    { icon: Zap, title: 'Fast deployment capability' },
    { icon: Users, title: 'Managed team options' },
    { icon: Shield, title: 'Compliance-aware delivery' },
    { icon: Settings, title: 'Flexible engagement models' },
    { icon: Target, title: 'Outcome-focused approach' },
  ];

  return (
    <section className="py-24 bg-slate-50 dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="mb-4 bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 hover:bg-blue-100">
            Why Choose Us
          </Badge>
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white mb-6">
            Why Clients{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              Work With Us
            </span>
          </h2>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reasons.map((item, index) => {
            const Icon = item.icon;
            return (
              <div key={index} className="flex items-center gap-4 p-6 bg-white dark:bg-slate-800 rounded-2xl shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg shadow-blue-500/20 flex-shrink-0">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <span className="text-lg font-medium text-slate-900 dark:text-white">{item.title}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: 'Andy Robert',
      company: 'Enterprise Client',
      text: "I can't say I'm surprised that Aurous Consultancy is the best IT solutions provider in the market. They've been around for 15 years and have an outstanding track record. Their customer service is second to none.",
      rating: 5
    },
    {
      name: 'Richard Yong',
      company: 'Tech Company',
      text: "I have been working with Aurous Consultancy for over two years now and they are the best IT solutions provider I have seen. They specialize in niche areas that most other companies lack in depth.",
      rating: 5
    },
    {
      name: 'Mohd Ruzlan Ahmad',
      company: 'Financial Services',
      text: "I was looking for the best IT solutions provider and Aurous Consultancy delivered everything with so much ease. They are amazing at what they do!",
      rating: 5
    },
    {
      name: 'Pawan Kumar',
      company: 'Insurance Sector',
      text: "I have been working with Aurous Consultancy for over 5 years now. They are a reliable IT solutions provider with superb customer service who always goes out of their way.",
      rating: 5
    },
  ];

  return (
    <section className="py-24 bg-white dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-yellow-100 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-400">
            Testimonials
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">
            What Our Clients Say
          </h2>
          <p className="text-slate-600 dark:text-slate-400">
            Trusted by leading companies across Asia Pacific
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {testimonials.map((testimonial, index) => (
            <Card key={index} className="border-0 shadow-lg bg-slate-50 dark:bg-slate-800">
              <CardContent className="p-6">
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                
                <p className="text-slate-600 dark:text-slate-400 text-sm mb-6 leading-relaxed line-clamp-4">
                  "{testimonial.text}"
                </p>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white text-sm">{testimonial.name}</p>
                    <p className="text-slate-500 dark:text-slate-400 text-xs">{testimonial.company}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Social Proof */}
        <div className="mt-16 flex flex-wrap justify-center gap-8">
          <div className="flex items-center gap-3 bg-slate-100 dark:bg-slate-800 rounded-2xl px-6 py-4">
            <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/53/Google_%22G%22_Logo.svg/512px-Google_%22G%22_Logo.svg.png" alt="Google" className="w-8 h-8" />
            <div>
              <p className="font-bold text-slate-900 dark:text-white">4.8 / 5.0</p>
              <p className="text-xs text-slate-500">76 Reviews</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-slate-100 dark:bg-slate-800 rounded-2xl px-6 py-4">
            <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/ca/LinkedIn_logo_initials.png/600px-LinkedIn_logo_initials.png" alt="LinkedIn" className="w-8 h-8" />
            <div>
              <p className="font-bold text-slate-900 dark:text-white">22,830+</p>
              <p className="text-xs text-slate-500">Followers</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-slate-100 dark:bg-slate-800 rounded-2xl px-6 py-4">
            <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/e/e1/Logo_of_Glassdoor_%282020%29.svg/1024px-Logo_of_Glassdoor_%282020%29.svg.png" alt="Glassdoor" className="w-8 h-8 object-contain" />
            <div>
              <p className="font-bold text-slate-900 dark:text-white">4.9 / 5.0</p>
              <p className="text-xs text-slate-500">25 Reviews</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// AI News Section
const AINewsSection = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API}/ai-news`);
        setNews(response.data.articles || []);
      } catch (error) {
        console.error('Failed to fetch news:', error);
        // Fallback to sample news if API fails
        setNews([
          {
            id: 1,
            title: "OpenAI Unveils GPT-5 with Revolutionary Reasoning Capabilities",
            description: "The latest iteration of OpenAI's language model demonstrates unprecedented problem-solving abilities and multimodal understanding.",
            source: "TechCrunch",
            publishedAt: new Date().toISOString(),
            url: "#",
            image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400"
          },
          {
            id: 2,
            title: "AI-Powered Code Generation Transforms Software Development",
            description: "New AI tools are enabling developers to write code 10x faster while maintaining quality and security standards.",
            source: "Wired",
            publishedAt: new Date(Date.now() - 86400000).toISOString(),
            url: "#",
            image: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400"
          },
          {
            id: 3,
            title: "Enterprise AI Adoption Reaches Record High in 2026",
            description: "Survey reveals 78% of Fortune 500 companies now use AI in core business operations, up from 52% last year.",
            source: "Forbes",
            publishedAt: new Date(Date.now() - 172800000).toISOString(),
            url: "#",
            image: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=400"
          },
          {
            id: 4,
            title: "AI in Healthcare: Machine Learning Detects Diseases Earlier",
            description: "New diagnostic AI systems can identify potential health issues up to 5 years before traditional methods.",
            source: "MIT Technology Review",
            publishedAt: new Date(Date.now() - 259200000).toISOString(),
            url: "#",
            image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400"
          },
          {
            id: 5,
            title: "Autonomous AI Agents Are Reshaping Business Operations",
            description: "Companies are deploying AI agents that can independently handle complex workflows and decision-making.",
            source: "Bloomberg",
            publishedAt: new Date(Date.now() - 345600000).toISOString(),
            url: "#",
            image: "https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=400"
          },
          {
            id: 6,
            title: "The Rise of AI-Native Startups: $50B Invested in 2025",
            description: "Venture capital pours into companies built entirely around artificial intelligence capabilities.",
            source: "VentureBeat",
            publishedAt: new Date(Date.now() - 432000000).toISOString(),
            url: "#",
            image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=400"
          }
        ]);
      }
      setLoading(false);
    };

    fetchNews();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <section id="ai-news" className="py-24 bg-slate-50 dark:bg-slate-900/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 mb-4">
            <Newspaper className="w-3 h-3 mr-1" />
            AI Insights
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            Latest in <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">AI News</span>
          </h2>
          <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto text-lg">
            Stay ahead of the curve with the latest developments in artificial intelligence, machine learning, and tech innovation.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {news.slice(0, 6).map((article, idx) => (
              <a
                key={article.id || idx}
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white dark:bg-slate-800 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-slate-200 dark:border-slate-700"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.image || article.urlToImage || `https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400`}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-3 left-3 flex items-center gap-2">
                    <Badge className="bg-white/90 text-slate-700 text-xs">
                      {article.source?.name || article.source}
                    </Badge>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 mb-3">
                    <Clock className="w-4 h-4" />
                    <span>{formatDate(article.publishedAt)}</span>
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2 line-clamp-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mb-4">
                    {article.description}
                  </p>
                  <div className="flex items-center text-purple-600 dark:text-purple-400 text-sm font-medium">
                    Read More
                    <ExternalLink className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}

        <div className="text-center mt-12">
          <Link to="/ai-news">
            <Button className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-full">
              View All AI News
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

// Strong CTA Section
const CTASection = () => {
  const { openChatbot } = useChatbot();
  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700"></div>
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }}></div>
      
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-8 backdrop-blur">
          <Rocket className="w-5 h-5 text-white" />
          <span className="text-white font-medium">Ready to Transform?</span>
        </div>
        
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
          Build Your AI-Ready
          <span className="block">Delivery Capability</span>
        </h2>
        
        <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
          Upgrade your technology delivery model with AI-powered teams and managed solutions. Talk to Aurous Consultancy today.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button onClick={() => openChatbot('booking')}>
            <Button size="lg" variant="secondary" className="text-blue-600 text-lg h-14 px-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              Schedule Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </button>
          <a href="#services">
            <Button size="lg" variant="outline" className="text-white border-white/30 hover:bg-white/10 text-lg h-14 px-8 rounded-2xl backdrop-blur">
              Explore Solutions
            </Button>
          </a>
          <button onClick={() => openChatbot('menu')}>
            <Button size="lg" variant="outline" className="text-white border-white/30 hover:bg-white/10 text-lg h-14 px-8 rounded-2xl backdrop-blur">
              Discuss Managed Teams
            </Button>
          </button>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', subject: '', message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white dark:bg-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left */}
          <div>
            <Badge className="mb-4 bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400">Contact Us</Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white mb-6">
              Let&apos;s Start a{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                Conversation
              </span>
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 mb-10">
              Have a project in mind? Need AI-ready teams or a managed delivery solution? We&apos;d love to hear from you. Send us a message and we&apos;ll respond within 24 hours.
            </p>

            <div className="space-y-6">
              {[
                { icon: Mail, label: 'Email', value: 'enquiry@aurousconsultancy.com' },
                { icon: Phone, label: 'Phone', value: '+60 3-8322 0562' },
                { icon: MapPin, label: 'Address', value: 'Cyberjaya, Selangor, Malaysia' },
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-2xl">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg shadow-blue-500/20">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{item.label}</p>
                      <p className="font-semibold text-slate-900 dark:text-white">{item.value}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Social Links */}
            <div className="mt-10">
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Follow us on social media</p>
              <div className="flex gap-3">
                {[Linkedin, Twitter, Facebook].map((Icon, i) => (
                  <a key={i} href="#" className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-900 flex items-center justify-center hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors">
                    <Icon className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div className="bg-slate-50 dark:bg-slate-900 rounded-3xl p-8 shadow-xl">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Name *</label>
                  <Input placeholder="Your name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="h-12 rounded-xl" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Email *</label>
                  <Input type="email" placeholder="you@company.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="h-12 rounded-xl" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Phone</label>
                  <Input placeholder="+60 12-345 6789" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="h-12 rounded-xl" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Subject *</label>
                  <Input placeholder="e.g., Team Request" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} className="h-12 rounded-xl" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Message *</label>
                <Textarea placeholder="Tell us about your requirements..." rows={5} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="rounded-xl" />
              </div>

              <Button type="submit" size="lg" className="w-full h-14 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-lg" disabled={loading}>
                {loading ? 'Sending...' : 'Send Message'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Main Website Component
export default function Website() {
  const location = useLocation();

  // Handle hash scrolling after page load
  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <Navbar />
      <HeroSection />
      <TrustedBySection />
      <AITechnologyShowcase />
      <ServicesSection />
      <DeliveryModelSection />
      <IndustriesSection />
      <RealResultsSection />
      <TechStackSection />
      <SecurityComplianceSection />
      <EngagementModelsSection />
      <EnterpriseProductsSection />
      <ConversionSection />
      <ContactSection />
      <WebsiteFooter />
    </div>
  );
}
